import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Taxonomy } from "@common/categories";
import { categoryCoaMappingContract } from "@common/contracts/category-coa-mapping.contract";
import {
    CategoryCoaMapping,
    CategoryCoaMappingsResponse,
} from "@common/schemas/domains/accounting/categoryCoaMapping.schema";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { didRequestReachServer } from "./apiError";

const categoryCoaMappingClient = getBackendAPIClient(
    categoryCoaMappingContract,
);

export const categoryCoaMappingQueryKeys = {
    all: ["category-coa-mappings"] as const,
    byEntity: (entityId: number) =>
        [...categoryCoaMappingQueryKeys.all, entityId] as const,
};

export function useCategoryCoaMappings(entityId: number) {
    return useQuery({
        queryKey: categoryCoaMappingQueryKeys.byEntity(entityId),
        queryFn: async (): Promise<CategoryCoaMappingsResponse> => {
            const response = await categoryCoaMappingClient.getMappings({
                params: { entityId },
            });
            return response.body;
        },
    });
}

interface UpdateCategoryCoaMappingVariables {
    taxonomy: Taxonomy;
    accountId: string | null;
}

export function useUpdateCategoryCoaMapping(entityId: number) {
    const queryClient = useQueryClient();
    const { error: toastError } = useToaster();
    const queryKey = categoryCoaMappingQueryKeys.byEntity(entityId);

    const replaceMapping = (updatedMapping: CategoryCoaMapping) =>
        queryClient.setQueryData<CategoryCoaMappingsResponse>(
            queryKey,
            (response) =>
                response && {
                    ...response,
                    mappings: response.mappings.map((mapping) =>
                        mapping.taxonomy === updatedMapping.taxonomy
                            ? updatedMapping
                            : mapping,
                    ),
                },
        );

    return useMutation({
        mutationFn: async ({
            taxonomy,
            accountId,
        }: UpdateCategoryCoaMappingVariables) => {
            const response = await categoryCoaMappingClient.updateMapping({
                params: { entityId, taxonomy },
                body: { accountId },
            });
            return response.body;
        },
        onMutate: async ({ taxonomy, accountId }) => {
            await queryClient.cancelQueries({ queryKey });
            const previousResponse =
                queryClient.getQueryData<CategoryCoaMappingsResponse>(queryKey);
            const previousMapping = previousResponse?.mappings.find(
                (mapping) => mapping.taxonomy === taxonomy,
            );
            if (previousMapping) {
                replaceMapping({
                    ...previousMapping,
                    accountId,
                    reasoning: null,
                });
            }
            return { previousResponse };
        },
        onSuccess: replaceMapping,
        onError: (error, _variables, context) => {
            if (context?.previousResponse) {
                queryClient.setQueryData(queryKey, context.previousResponse);
            }
            if (!didRequestReachServer(error)) {
                toastError("Failed to update category mapping");
            }
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey }),
    });
}
